import { useCallback, useMemo } from "react"
import { Flex } from "../../Flex"
import * as styles from "../styles/selectStyles"
import { CellProps } from "../types"

type DTI = {
  selectAll?: string
  selectSingle?: string
}

export const useSelectColumn = <T extends Record<string, unknown> & { id: string | null }>(
  data: ReadonlyArray<T>,
  accessor: keyof T,
  selectedRows: ReadonlyArray<T>,
  onSelectedRowsChanged: (selectedRows: ReadonlyArray<T>) => void,
  dataTestIds?: DTI
) => {
  const selectedIds = useMemo(() => selectedRows.map(({ id }) => id), [selectedRows])

  const wholePageChecked = useMemo(
    () => (data.length ? data.every((it) => selectedIds.includes(it.id)) : false),
    [data, selectedIds]
  )
  const SelectAllCell = useCallback(
    () => (
      <Flex css={styles.checkboxContainer}>
        <input
          type="checkbox"
          data-testid={dataTestIds?.selectAll}
          checked={wholePageChecked}
          onChange={() => {
            if (wholePageChecked) {
              const ids = data.map(({ id }) => id)
              onSelectedRowsChanged([...selectedRows.filter((r) => !ids.includes(r.id))])
            } else {
              onSelectedRowsChanged([...selectedRows, ...data])
            }
          }}
        />
      </Flex>
    ),
    [onSelectedRowsChanged, data, dataTestIds?.selectAll, selectedRows, wholePageChecked]
  )

  const selectRow = useCallback(
    (id: unknown) => {
      if (typeof id !== `string`) return

      const newRows = [...selectedRows]
      const selectedIndex = selectedRows.findIndex((row) => row.id === id)

      if (selectedIndex > -1) {
        newRows.splice(selectedIndex, 1)
        onSelectedRowsChanged([...newRows])

        return
      }

      const row = data.find((d) => d.id === id)

      if (row) {
        newRows.push(row)
        onSelectedRowsChanged(newRows)
      }
    },
    [onSelectedRowsChanged, selectedRows, data]
  )

  const SelectCell = useCallback(
    ({ row }: CellProps<string, T>) => {
      const it = row.original
      return (
        <Flex css={styles.checkboxContainer}>
          <input
            type="checkbox"
            data-testid={dataTestIds?.selectSingle}
            checked={selectedIds.includes(it.id)}
            onChange={() => {
              selectRow(it.id)
            }}
          />
        </Flex>
      )
    },
    [selectRow, dataTestIds?.selectSingle, selectedIds]
  )

  return {
    selectColumn: {
      Header: SelectAllCell,
      Cell: SelectCell,
      accessor,
      width: 10,
    },
    selectRow,
  }
}
